/**
 * Web-only styles for View Requests `createPortal` filter/sort dropdowns (#1047).
 * Uses existing `src/theme` exports on `development` — no parallel token modules.
 */
import {
  colors,
  fontSize,
  fontWeight,
  layout,
  letterSpacing,
  spacing,
} from '../theme';
import { elevation } from '../theme/elevation';

import type { CSSProperties } from 'react';

const vr = layout.viewRequests;

const borderSolid = (color: string): string => `1px solid ${color}`;

function portalDropdownBoxShadow(): string {
  const webPopover = elevation.popover as CSSProperties;
  return (
    (webPopover.boxShadow as string | undefined) ??
    '0 4px 12px rgba(0, 0, 0, 0.25)'
  );
}

/**
 * The panel, sized to the room BELOW THE BUTTON rather than to a constant
 * (#1640).
 *
 * ⚠️ IT WAS A FIXED 300px, AND THAT IS A BUG THE NEXT SECTION ALWAYS PAYS FOR.
 * The filter panel's content is a stack of sections — Read Status, Discussion,
 * Region, Stage, Modified On — about 890px of it, inside a box that was 300px
 * tall WHATEVER the window could offer. Everything past the first two sections
 * sat in an inner scroller with no visible affordance (macOS hides overlay
 * scrollbars until you scroll), so adding the Discussion section pushed
 * "Without discussion" exactly one row past the fold and it read as a MISSING
 * OPTION. Worse, Region and Stage were already down there: a filter set earlier
 * stayed invisible while it narrowed the list, which looks like a broken filter
 * rather than an unscrolled panel.
 *
 * `maxHeight` therefore comes from the caller, which measures
 * `window.innerHeight - rect.bottom` when it positions the panel. The constant
 * remains as the fallback for a caller that cannot measure (and for the unit
 * test's fixed-position cases).
 *
 * ⚠️ FLEX COLUMN, AND THE SCROLL AREA MUST BE `flex: 1` FOR IT TO MEAN
 * ANYTHING. The header is fixed and the scroll area takes the rest; without
 * `minHeight: 0` a flex child refuses to shrink below its content and the
 * panel grows past its own `maxHeight` instead of scrolling inside it.
 */
export function getDropdownPortalRootStyle(pos: {
  top: number;
  left: number;
  width: number;
  /** Space available below the button, measured by the caller. */
  maxHeight?: number;
}): CSSProperties {
  return {
    position: 'absolute',
    top: pos.top,
    left: pos.left,
    width: pos.width,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.backgroundPrimary,
    border: borderSolid(colors.borderMedium),
    borderRadius: `${vr.dropdownBorderRadius}px`,
    maxHeight: `${pos.maxHeight ?? vr.dropdownMaxHeight}px`,
    boxShadow: portalDropdownBoxShadow(),
    zIndex: vr.zIndexPortal,
    overflow: 'hidden',
  };
}

/**
 * How tall the panel may be, given where its button sits (#1640).
 *
 * Exported and pure so the screen's two position updaters cannot drift from
 * each other, and so the arithmetic is testable without a DOM.
 *
 * ⚠️ THE FLOOR IS NOT COSMETIC. On a very short window — or with the button
 * near the bottom — the available space can go to nothing, and a 0px panel is
 * an invisible one. Below the floor the panel keeps its height and scrolls
 * internally, which is the old behaviour and still usable.
 */
export function dropdownPanelMaxHeight(
  buttonBottom: number,
  viewportHeight: number,
): number {
  return Math.max(
    vr.dropdownMinHeight,
    viewportHeight - buttonBottom - vr.dropdownViewportGutter,
  );
}

export const dropdownPortalHeaderRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${spacing.md}px ${spacing.lg}px`,
  borderBottom: borderSolid(colors.borderLight),
};

export const dropdownPortalTitle: CSSProperties = {
  fontSize: `${fontSize.subheading}px`,
  fontWeight: fontWeight.semibold,
  color: colors.textPrimary,
};

export const dropdownPortalHeaderButton: CSSProperties = {
  background: 'none',
  border: 'none',
  color: colors.primary,
  fontSize: `${fontSize.formControl}px`,
  fontWeight: fontWeight.medium,
  cursor: 'pointer',
};

/**
 * ⚠️ `flex: 1` AND `minHeight: 0`, NOT A FIXED `maxHeight` (#1640). The panel
 * now decides its own height from the room below the button; this takes
 * whatever is left under the header. The old `maxHeight` would have capped the
 * list at 250px inside a panel three times that tall.
 */
export const dropdownPortalScrollArea: CSSProperties = {
  padding: `${spacing.sm}px 0`,
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
};

export const filterSectionPadding: CSSProperties = {
  padding: `${spacing.sm}px ${spacing.lg}px`,
};

export const filterSectionHeaderRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: `${spacing.sm}px`,
};

export const filterSectionLabel: CSSProperties = {
  fontSize: `${fontSize.charCount}px`,
  fontWeight: fontWeight.semibold,
  color: colors.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: `${letterSpacing.fieldLabelUppercase}px`,
};

export const filterSectionClearButton: CSSProperties = {
  background: 'none',
  border: 'none',
  color: colors.primary,
  fontSize: `${fontSize.charCount}px`,
  fontWeight: fontWeight.medium,
  cursor: 'pointer',
  textTransform: 'uppercase',
  letterSpacing: `${letterSpacing.fieldLabelUppercase}px`,
};

export function getFilterOptionRowStyle(isSelected: boolean): CSSProperties {
  return {
    padding: `${spacing.sm}px ${spacing.md}px`,
    borderRadius: `${Math.max(4, vr.dropdownBorderRadius - 2)}px`,
    cursor: 'pointer',
    backgroundColor: isSelected ? colors.surfaceAccentMuted : 'transparent',
    color: isSelected ? colors.primary : colors.textPrimary,
    fontWeight: isSelected ? fontWeight.medium : 'normal',
  };
}

export function getSortOptionRowStyle(isSelected: boolean): CSSProperties {
  return {
    padding: `${spacing.sm}px ${spacing.lg}px`,
    cursor: 'pointer',
    backgroundColor: isSelected ? colors.surfaceAccentMuted : 'transparent',
    color: isSelected ? colors.primary : colors.textPrimary,
    fontWeight: isSelected ? fontWeight.medium : 'normal',
  };
}

export const sortDirectionHint: CSSProperties = {
  fontSize: `${fontSize.charCount}px`,
  color: colors.textSecondary,
  fontWeight: '400',
};
